<template>
  <v-dialog v-model="dialog" transition="dialog-bottom-transition" scrollable max-width="560">
    <template v-if="!hideButton" v-slot:activator="{ props }">
      <v-btn
        color="secondary"
        :class="{ 'float-right': floatRight, 'mx-2': floatRight }"
        v-bind="props"
      >
        <v-icon class="mr-2">mdi-store-plus</v-icon>
        Alocar na loja
      </v-btn>
    </template>
    <v-card>
      <v-toolbar color="primary" :title="toolbarTitle">
        <v-btn color="black" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>
      <v-card-text>
        <v-alert type="info" variant="tonal" density="compact" class="mb-3">
          Alocação temporária do pool do grupo. Cada loja ≤ estoque do grupo; a soma entre lojas
          pode ultrapassar o grupo (over-assign).
        </v-alert>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="form.store_id"
              :items="storeItems"
              item-title="title"
              item-value="value"
              label="Loja"
              :rules="[(v) => !!v || 'Campo obrigatório']"
              :loading="loadingStores"
              :disabled="requestingApi || lockStore"
              density="compact"
              clearable
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12">
            <v-autocomplete
              v-model="form.product_id"
              :items="productItems"
              item-title="title"
              item-value="value"
              label="Produto"
              :rules="[(v) => !!v || 'Campo obrigatório']"
              :loading="loadingProducts"
              :disabled="requestingApi || lockProduct"
              density="compact"
              clearable
              hide-details="auto"
              @update:model-value="onProductPicked"
            />
          </v-col>
          <v-col v-if="needsVariant" cols="12">
            <v-select
              v-model="form.product_variant_id"
              :items="variantItems"
              item-title="title"
              item-value="value"
              label="Variação"
              :rules="[(v) => !!v || 'Selecione a variação']"
              :loading="loadingVariants"
              :disabled="requestingApi || lockVariant"
              density="compact"
              clearable
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.mode"
              :items="modeItems"
              item-title="title"
              item-value="value"
              label="Operação"
              :disabled="requestingApi"
              density="compact"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.quantity"
              :label="quantityLabel"
              type="number"
              min="0"
              step="1"
              :rules="[
                (v) =>
                  (v !== null && v !== undefined && v !== '' && Number(v) >= 0) ||
                  'Informe quantidade ≥ 0',
              ]"
              :disabled="requestingApi"
              density="compact"
              hide-details="auto"
            />
          </v-col>
          <v-col v-if="form.mode === 'allocate'" cols="12">
            <v-switch
              color="primary"
              label="Quantidade absoluta (definir claim da loja)"
              v-model="form.absolute"
              :disabled="requestingApi"
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.note"
              label="Observação"
              density="compact"
              :disabled="requestingApi"
              clearable
              hide-details="auto"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn variant="text" color="error" @click="dialog = false">Cancelar</v-btn>
        <v-btn
          color="success"
          :loading="requestingApi"
          :disabled="requestingApi"
          @click="submit"
        >
          <v-icon class="mr-2">mdi-content-save</v-icon>
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import api from '@/services'
import { normalizeListPayload } from '@/utils/listPayload'

function emptyForm() {
  return {
    store_id: null,
    product_id: null,
    product_variant_id: null,
    mode: 'allocate',
    quantity: null,
    absolute: true,
    note: null,
    product: null,
  }
}

export default {
  props: {
    user: Object,
    /** Prefill { store_id, product_id, product_variant_id, product?, variant?, quantity?, mode? } */
    modelValue: Object,
    /** When set, store is fixed (e.g. from StoreDetail) */
    storeId: [Number, String],
    floatRight: Boolean,
    hideButton: Boolean,
  },
  emits: ['update:modelValue', 'done'],
  data() {
    return {
      dialog: false,
      requestingApi: false,
      loadingStores: false,
      loadingProducts: false,
      loadingVariants: false,
      stores: [],
      products: [],
      variants: [],
      form: emptyForm(),
      modeItems: [
        { title: 'Alocar (claim na loja)', value: 'allocate' },
        { title: 'Desalocar (devolver ao grupo)', value: 'deallocate' },
      ],
    }
  },
  computed: {
    toolbarTitle() {
      return this.form.mode === 'deallocate' ? 'Desalocar estoque' : 'Alocar estoque na loja'
    },
    quantityLabel() {
      if (this.form.mode === 'deallocate') return 'Quantidade a desalocar'
      return this.form.absolute ? 'Claim da loja (absoluto)' : 'Quantidade a adicionar'
    },
    lockStore() {
      return !!(this.storeId || this.modelValue?.store_id)
    },
    lockProduct() {
      return !!(this.modelValue?.product_id || this.modelValue?.product?.id)
    },
    lockVariant() {
      return !!(this.modelValue?.product_variant_id || this.modelValue?.variant?.id)
    },
    needsVariant() {
      if (this.variants.length > 1) return true
      if (this.variants.length === 1 && !this.variants[0].is_default) return true
      return !!(this.form?.product_variant_id && this.variants.length)
    },
    storeItems() {
      return (this.stores || []).map((s) => ({
        title: s.name,
        value: s.id,
      }))
    },
    productItems() {
      const items = (this.products || []).map((p) => ({
        title: p.name,
        value: p.id,
        product: p,
      }))
      const current = this.form?.product || this.modelValue?.product
      if (current?.id && !items.some((i) => i.value === current.id)) {
        items.unshift({ title: current.name, value: current.id, product: current })
      }
      return items
    },
    variantItems() {
      return (this.variants || [])
        .filter((v) => v.active !== false)
        .map((v) => ({
          title: this.variantTitle(v),
          value: v.id,
        }))
    },
  },
  methods: {
    variantTitle(v) {
      if (!v) return '—'
      if (v.name_suffix) return v.name_suffix
      if (v.is_default) return 'Padrão'
      if (Array.isArray(v.options) && v.options.length) {
        return v.options.map((o) => o.value).join(' / ')
      }
      return `Variação #${v.id}`
    },
    resetForm() {
      this.form = emptyForm()
      if (this.storeId != null) this.form.store_id = Number(this.storeId)
      this.variants = []
    },
    hydrateFromModel(model) {
      if (!model) {
        this.resetForm()
        return
      }
      this.form = {
        store_id:
          model.store_id != null
            ? Number(model.store_id)
            : this.storeId != null
              ? Number(this.storeId)
              : null,
        product_id: model.product_id || model.product?.id || null,
        product_variant_id: model.product_variant_id || model.variant?.id || null,
        mode: model.mode === 'deallocate' ? 'deallocate' : 'allocate',
        quantity: model.quantity != null ? model.quantity : null,
        absolute: model.absolute !== false,
        note: model.note || null,
        product: model.product || null,
      }
      this.loadVariantsForProduct(this.form.product_id)
    },
    onProductPicked() {
      this.form.product_variant_id = null
      this.loadVariantsForProduct(this.form.product_id)
    },
    loadVariantsForProduct(productId) {
      const vm = this
      vm.variants = []
      if (!productId) return Promise.resolve()
      vm.loadingVariants = true
      return api.productVariant
        .list({ product_id: Number(productId), active: true })
        .then((result) => {
          const { items } = normalizeListPayload(result)
          vm.variants = Array.isArray(result)
            ? result
            : Array.isArray(items)
              ? items
              : []
          if (
            !vm.form.product_variant_id &&
            vm.variants.length === 1 &&
            vm.variants[0].is_default
          ) {
            vm.form.product_variant_id = vm.variants[0].id
          }
        })
        .catch(() => {
          vm.variants = []
        })
        .finally(() => {
          vm.loadingVariants = false
        })
    },
    loadStores() {
      const vm = this
      if (vm.storeId != null || vm.lockStore) return Promise.resolve()
      if (vm.loadingStores) return Promise.resolve()
      vm.loadingStores = true
      return api.store
        .list({ status: 'active', itemsPerPage: 200 })
        .then((result) => {
          const { items } = normalizeListPayload(result)
          vm.stores = items || []
        })
        .catch((err) => {
          if (err?.message) vm.$notify({ type: 'warn', text: err.message })
        })
        .finally(() => {
          vm.loadingStores = false
        })
    },
    loadProducts() {
      const vm = this
      if (vm.loadingProducts) return Promise.resolve()
      vm.loadingProducts = true
      return api.product
        .list({ active: true, tracks_stock: true, itemsPerPage: 200 })
        .then((result) => {
          const { items } = normalizeListPayload(result)
          vm.products = items || []
        })
        .catch((err) => {
          if (err?.message) vm.$notify({ type: 'warn', text: err.message })
        })
        .finally(() => {
          vm.loadingProducts = false
        })
    },
    buildPayload() {
      const f = this.form || {}
      const payload = {
        store_id: Number(f.store_id),
        quantity: Number(f.quantity),
      }
      if (f.product_variant_id != null && f.product_variant_id !== '') {
        payload.product_variant_id = Number(f.product_variant_id)
      }
      if (f.product_id != null && f.product_id !== '') {
        payload.product_id = Number(f.product_id)
      }
      if (f.mode === 'allocate') {
        payload.absolute = !!f.absolute
      }
      if (f.note) payload.note = String(f.note)
      return payload
    },
    submit() {
      const vm = this
      if (vm.requestingApi) return
      if (!vm.form?.store_id) {
        vm.$notify({ type: 'warn', text: 'Selecione a loja' })
        return
      }
      if (!vm.form?.product_id && !vm.form?.product_variant_id) {
        vm.$notify({ type: 'warn', text: 'Selecione um produto' })
        return
      }
      if (vm.needsVariant && !vm.form.product_variant_id) {
        vm.$notify({ type: 'warn', text: 'Selecione a variação' })
        return
      }
      const qty = Number(vm.form.quantity)
      if (isNaN(qty) || qty < 0) {
        vm.$notify({ type: 'warn', text: 'Informe quantidade ≥ 0' })
        return
      }
      if (vm.form.mode === 'deallocate' && !(qty > 0)) {
        vm.$notify({ type: 'warn', text: 'Informe quantidade a desalocar > 0' })
        return
      }
      vm.requestingApi = true
      const payload = vm.buildPayload()
      const req =
        vm.form.mode === 'deallocate'
          ? api.stock.deallocate(payload)
          : api.stock.allocate(payload)
      return req
        .then((result) => {
          vm.$emit('done', result)
          vm.$notify({
            type: 'success',
            text:
              vm.form.mode === 'deallocate'
                ? 'Estoque desalocado'
                : 'Estoque alocado na loja',
          })
          vm.dialog = false
        })
        .catch((err) => {
          if (err?.message) vm.$notify({ type: 'warn', text: err.message })
        })
        .finally(() => {
          vm.requestingApi = false
        })
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal && (newVal.product_id || newVal.product?.id || newVal.store_id) && !this.dialog) {
        this.hydrateFromModel(newVal)
        this.dialog = true
      }
    },
    dialog(newVal) {
      if (newVal) {
        if (
          this.modelValue &&
          (this.modelValue.product_id ||
            this.modelValue.product?.id ||
            this.modelValue.store_id ||
            this.modelValue.product_variant_id)
        ) {
          this.hydrateFromModel(this.modelValue)
        } else {
          this.resetForm()
        }
        this.loadStores()
        this.loadProducts()
      } else {
        this.resetForm()
        this.$emit('update:modelValue', {})
      }
    },
    storeId(newVal) {
      if (this.form && newVal != null) this.form.store_id = Number(newVal)
    },
  },
}
</script>
